import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, API, tenantHeaders, apiErr } from "../lib/api";
import { fmt } from "../lib/format";
import { categoryImage } from "../lib/assets";
import { CartProvider, useCart } from "../context/CartContext";
import { BrandLogo } from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Badge } from "../components/ui/badge";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Search, ShoppingBag, Clock, MapPin, Phone, Plus, Minus, Trash2, X, Loader2, Ban, Download, Tag, Heart } from "lucide-react";
import { useAuth } from "../context/AuthContext";

function promoLabel(p) {
  return p.type === "percentage" ? `${p.value}% di sconto` : p.type === "fixed" ? `${fmt.eur(p.value)} di sconto` : "consegna gratis";
}

function useStore(slug, storeSlug) {
  const [config, setConfig] = useState(null);
  const [menu, setMenu] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const [c, m] = await Promise.all([
          api.get("/store/config", tenantHeaders(slug, storeSlug)),
          api.get("/store/menu", tenantHeaders(slug, storeSlug)),
        ]);
        setConfig(c.data); setMenu(m.data);
      } catch (e) { setErr(apiErr(e)); }
    })();
  }, [slug, storeSlug]);
  return { config, menu, err };
}

function StorePicker({ slug, stores, tenant, branding }) {
  return (
    <div className="min-h-screen bg-[hsl(var(--secondary))] flex flex-col items-center justify-center p-6" style={{ "--brand": branding?.primary_color || "#FF7A00" }}>
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <BrandLogo name={tenant?.commercial_name} logoUrl={branding?.logo_url} color={branding?.primary_color} size={44} />
          <h1 className="font-head text-2xl font-bold mt-4">Scegli il punto vendita</h1>
          <p className="text-sm text-muted-foreground mt-1">Seleziona la sede da cui vuoi ordinare</p>
        </div>
        <div className="space-y-3">
          {stores.map((s) => (
            <Link key={s.slug} to={`/t/${slug}/${s.slug}`} data-testid={`pick-store-${s.slug}`}
                  className="flex items-center gap-3 bg-card border rounded-2xl p-4 hover:-translate-y-0.5 transition-transform">
              <div className="w-11 h-11 rounded-xl brand-soft flex items-center justify-center"><MapPin className="brand-text" size={20} /></div>
              <div className="flex-1"><p className="font-semibold">{s.name}</p><p className="text-xs text-muted-foreground">{s.address}</p></div>
              <span className="brand-text font-semibold text-sm">Ordina →</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function ThemeApplier({ color, children }) {
  return <div style={{ "--brand": color || "#FF7A00" }}>{children}</div>;
}

function StatusPill({ open }) {
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${open ? "bg-[hsl(var(--success))] text-white" : "bg-muted text-muted-foreground"}`}>
      {open ? "Aperto ora" : "Chiuso"}
    </span>
  );
}

function ProductDialog({ product, onClose, onAdd }) {
  const [selected, setSelected] = useState({});
  const [note, setNote] = useState("");
  const [qty, setQty] = useState(1);
  const [imgIdx, setImgIdx] = useState(0);
  const imgs = (product.images && product.images.length) ? product.images : [product.image_url || categoryImage(product.name)];
  const groups = product.option_groups || [];

  const toggle = (g, optId) => {
    setSelected((prev) => {
      const cur = prev[g.id] || [];
      if (g.type === "single") return { ...prev, [g.id]: [optId] };
      const exists = cur.includes(optId);
      if (exists) return { ...prev, [g.id]: cur.filter((x) => x !== optId) };
      if (cur.length >= (g.max || 99)) return prev;
      return { ...prev, [g.id]: [...cur, optId] };
    });
  };

  const unit = useMemo(() => {
    let p = product.price;
    groups.forEach((g) => (selected[g.id] || []).forEach((oid) => {
      const o = g.options.find((x) => x.id === oid); if (o) p += o.price || 0;
    }));
    return p;
  }, [selected, product, groups]);

  const add = () => {
    for (const g of groups) {
      if (g.required && g.type === "single" && !(selected[g.id]?.length)) {
        toast.error(`Seleziona: ${g.name}`); return;
      }
    }
    const options = [];
    groups.forEach((g) => (selected[g.id] || []).forEach((oid) => {
      const o = g.options.find((x) => x.id === oid);
      options.push({ group_id: g.id, group_name: g.name, option_id: oid, option_name: o.name, price: o.price || 0, removable: o.removable || false });
    }));
    onAdd({ product_id: product.id, name: product.name, unit_price: product.price, quantity: qty, options, note, line_total: unit });
    onClose();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md p-0 overflow-hidden gap-0 max-h-[90vh] flex flex-col">
        <div className="relative">
          <img src={imgs[imgIdx]} alt={product.name} className="h-48 w-full object-cover" />
          {imgs.length > 1 && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 bg-black/30 rounded-full px-2 py-1">
              {imgs.map((im, i) => <button key={i} onClick={() => setImgIdx(i)} data-testid={`gallery-dot-${i}`} className={`w-2 h-2 rounded-full transition-colors ${i === imgIdx ? "bg-white" : "bg-white/60"}`} />)}
            </div>
          )}
        </div>
        <div className="p-5 overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head text-xl">{product.name}</DialogTitle></DialogHeader>
          {product.description && <p className="text-sm text-muted-foreground mt-1">{product.description}</p>}
          {product.allergens?.length > 0 && <p className="text-xs text-muted-foreground mt-2">Allergeni: {product.allergens.join(", ")}</p>}

          <div className="mt-4 space-y-5">
            {groups.map((g) => (
              <div key={g.id}>
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-sm">{g.name}</h4>
                  {g.required ? <Badge variant="secondary" className="text-[10px]">Obbligatorio</Badge> : <span className="text-[11px] text-muted-foreground">Facoltativo</span>}
                </div>
                <div className="mt-2 space-y-1.5">
                  {g.options.map((o) => {
                    const active = (selected[g.id] || []).includes(o.id);
                    return (
                      <button key={o.id} data-testid={`opt-${o.id}`} onClick={() => toggle(g, o.id)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl border text-sm transition-colors ${active ? "brand-border brand-soft" : "hover:bg-muted"}`}>
                        <span className="flex items-center gap-2">
                          <span className={`w-4 h-4 ${g.type === "single" ? "rounded-full" : "rounded"} border flex items-center justify-center ${active ? "brand-bg brand-border" : ""}`} />
                          {o.removable ? `Senza ${o.name.replace(/^Senza /i, "")}` : o.name}
                        </span>
                        {o.price > 0 && <span className="text-muted-foreground">+{fmt.eur(o.price)}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            <Textarea placeholder="Nota per questo prodotto (es. poco tostato)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="product-note" />
          </div>
        </div>
        <div className="p-4 border-t flex items-center gap-3 bg-card">
          <div className="flex items-center gap-3 border rounded-full px-2 py-1">
            <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-minus"><Minus size={16} /></button>
            <span className="w-5 text-center font-semibold">{qty}</span>
            <button onClick={() => setQty(qty + 1)} data-testid="qty-plus"><Plus size={16} /></button>
          </div>
          <Button onClick={add} data-testid="add-to-cart-btn" className="flex-1 h-12 brand-bg hover:opacity-90 rounded-full font-semibold">
            Aggiungi · {fmt.eur(unit * qty)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CartSheet({ open, onClose, slug, storeSlug, menu }) {
  const cart = useCart();
  const nav = useNavigate();
  const checkoutPath = storeSlug ? `/t/${slug}/${storeSlug}/checkout` : `/t/${slug}/checkout`;
  const inCart = new Set(cart.items.map((i) => i.product_id));
  const PRIORITY = ["bevand", "bibit", "drink", "dolc", "dessert", "gelat", "caff", "contorn", "frit", "side", "snack", "acqua"];
  const catName = (id) => (menu?.categories?.find((c) => c.id === id)?.name || "").toLowerCase();
  const suggestions = (menu?.products || [])
    .filter((p) => !inCart.has(p.id) && !p.archived && !(p.option_groups || []).some((g) => g.required))
    .sort((a, b) => (PRIORITY.some((k) => catName(a.category_id).includes(k)) ? 0 : 1) - (PRIORITY.some((k) => catName(b.category_id).includes(k)) ? 0 : 1))
    .slice(0, 4);
  const addSuggestion = (p) => cart.addItem({ product_id: p.id, name: p.name, unit_price: p.price, quantity: 1, options: [], note: "", line_total: p.price });
  const [smart, setSmart] = useState([]);
  useEffect(() => {
    if (!open || cart.items.length === 0) { setSmart([]); return; }
    const pids = cart.items.map((i) => i.product_id).join(",");
    api.get("/store/suggestions", { params: { store: storeSlug || "", product_ids: pids }, headers: { "X-Tenant-Slug": slug } })
      .then((r) => setSmart(r.data || [])).catch(() => setSmart([]));
  }, [open, cart.items.length, slug, storeSlug]);
  const finalSuggestions = ((smart && smart.length ? smart : suggestions) || []).filter((p) => !inCart.has(p.id)).slice(0, 4);
  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="p-5 border-b"><SheetTitle className="font-head">Il tuo ordine</SheetTitle></SheetHeader>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {cart.items.length === 0 && <p className="text-muted-foreground text-center py-12">Il carrello è vuoto</p>}
          {cart.items.map((it) => (
            <div key={it.cid} className="flex gap-3 border-b pb-3" data-testid="cart-line">
              <div className="flex-1">
                <p className="font-semibold text-sm">{it.name}</p>
                {it.options?.map((o, i) => <p key={i} className="text-xs text-muted-foreground">{o.removable ? "− " : "+ "}{o.option_name}</p>)}
                {it.note && <p className="text-xs italic">“{it.note}”</p>}
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex items-center gap-2 border rounded-full px-2 py-0.5">
                    <button onClick={() => cart.updateQty(it.cid, it.quantity - 1)}><Minus size={14} /></button>
                    <span className="text-sm w-4 text-center">{it.quantity}</span>
                    <button onClick={() => cart.updateQty(it.cid, it.quantity + 1)}><Plus size={14} /></button>
                  </div>
                  <button onClick={() => cart.removeItem(it.cid)} className="text-destructive"><Trash2 size={16} /></button>
                </div>
              </div>
              <div className="font-semibold text-sm">{fmt.eur(it.line_total * it.quantity)}</div>
            </div>
          ))}
          {cart.items.length > 0 && finalSuggestions.length > 0 && (
            <div className="pt-1" data-testid="cross-sell">
              <p className="font-head font-semibold text-sm mb-2">Ti potrebbe interessare anche</p>
              <div className="space-y-2">
                {finalSuggestions.map((p) => (
                  <div key={p.id} className="flex items-center justify-between gap-2 border rounded-xl p-2" data-testid={`suggestion-${p.id}`}>
                    <div className="min-w-0"><p className="text-sm font-medium truncate">{p.name}</p><p className="text-xs text-muted-foreground">{fmt.eur(p.price)}</p></div>
                    <Button size="sm" variant="outline" className="rounded-full h-8 shrink-0" onClick={() => addSuggestion(p)} data-testid={`suggestion-add-${p.id}`}><Plus size={14} className="mr-1" />Aggiungi</Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {cart.items.length > 0 && (
          <div className="p-5 border-t">
            <div className="flex justify-between font-head text-lg font-bold mb-3"><span>Totale</span><span>{fmt.eur(cart.total)}</span></div>
            <Button data-testid="go-checkout" onClick={() => { onClose(); nav(checkoutPath); }}
                    className="w-full h-12 brand-bg hover:opacity-90 rounded-full font-semibold">Vai al checkout</Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function StorefrontInner({ slug, storeSlug }) {
  const { config, menu, err } = useStore(slug, storeSlug);
  const cart = useCart();
  const { user } = useAuth();
  const isCustomer = user && user.role === "customer";
  const [fav, setFav] = useState(false);
  const favKey = config?.store_slug || storeSlug;
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [product, setProduct] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [installEvt, setInstallEvt] = useState(null);
  const [promoClosed, setPromoClosed] = useState(false);

  useEffect(() => {
    const h = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener("beforeinstallprompt", h);
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, []);

  useEffect(() => {
    const c = new URLSearchParams(window.location.search).get("coupon");
    if (c) localStorage.setItem("pf_prefill_coupon", c.toUpperCase());
  }, []);

  useEffect(() => {
    if (!isCustomer || !favKey) { setFav(false); return; }
    api.get("/account/favorites").then((r) => setFav(r.data.some((f) => f.tenant_slug === slug && f.store_slug === favKey))).catch(() => {});
  }, [isCustomer, slug, favKey]);

  const toggleFav = async () => {
    if (!isCustomer) { toast.error("Accedi come cliente per salvare i preferiti"); return; }
    const has = fav; setFav(!has);
    try {
      if (has) await api.delete(`/account/favorites/${slug}/${favKey}`);
      else { await api.post("/account/favorites", { tenant_slug: slug, store_slug: favKey }); toast.success("Aggiunto ai preferiti"); }
    } catch (e) { setFav(has); toast.error(apiErr(e)); }
  };

  useEffect(() => {
    if (!config || config.suspended) return;
    const color = config.branding?.primary_color || "#FF7A00";
    let link = document.querySelector('link[rel="manifest"]');
    if (!link) { link = document.createElement("link"); link.rel = "manifest"; document.head.appendChild(link); }
    link.href = `${API}/store/manifest?tenant=${slug}`;
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "theme-color"; document.head.appendChild(meta); }
    meta.content = color;
    let ai = document.querySelector('link[rel="apple-touch-icon"]');
    if (!ai) { ai = document.createElement("link"); ai.rel = "apple-touch-icon"; document.head.appendChild(ai); }
    ai.href = config.branding?.logo_url || "/pickfood-icon.svg";
    document.title = `${config.tenant.commercial_name} · Ordina online`;
    if ("serviceWorker" in navigator) navigator.serviceWorker.register("/sw.js").catch(() => {});
  }, [config, slug]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">{err}</div>;
  if (!config || !menu) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin brand-text" /></div>;
  if (config.suspended) return <div className="min-h-screen flex items-center justify-center text-center p-8"><div><Ban className="mx-auto mb-3 text-muted-foreground" size={40} /><h1 className="font-head text-2xl font-bold">Servizio sospeso</h1><p className="text-muted-foreground mt-2">Questo locale non è al momento disponibile.</p></div></div>;
  if (!storeSlug && config.stores && config.stores.length > 1)
    return <StorePicker slug={slug} stores={config.stores} tenant={config.tenant} branding={config.branding} />;

  const { tenant, store, branding } = config;
  const cats = menu.categories;
  const products = menu.products.filter((p) => (!q || p.name.toLowerCase().includes(q.toLowerCase())) && (!active || p.category_id === active));

  return (
    <ThemeApplier color={branding?.primary_color}>
      <div className="min-h-screen bg-[hsl(var(--secondary))] pb-28">
        {/* header */}
        <div className="relative">
          <div className="h-44 sm:h-56 brand-soft overflow-hidden relative">
            <img src={branding?.cover_url || categoryImage(tenant.template)} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent" />
            <div className="absolute bottom-3 left-0 right-0 max-w-3xl mx-auto px-4 flex items-center gap-3">
              {branding?.logo_url && (
                <div className="w-14 h-14 rounded-xl bg-white shadow-lg overflow-hidden shrink-0 ring-2 ring-white/70" data-testid="storefront-logo">
                  <img src={branding.logo_url} alt={store.name} className="w-full h-full object-contain p-1" />
                </div>
              )}
              <div className="min-w-0">
                <h1 className="font-head text-xl sm:text-2xl font-bold text-white drop-shadow truncate">{store.name || tenant.commercial_name}</h1>
                {(store.name && tenant.commercial_name && store.name !== tenant.commercial_name) && <p className="text-white/85 text-xs sm:text-sm drop-shadow truncate">{tenant.commercial_name}</p>}
              </div>
            </div>
          </div>
          <div className="max-w-3xl mx-auto px-4 mt-4 relative">
            <div className="bg-card rounded-2xl border shadow-[0_8px_30px_rgb(0,0,0,0.06)] p-5">
              <div className="flex items-center justify-between gap-3">
                <StatusPill open={config.store.open && !config.store.paused} />
                <div className="flex items-center gap-2">
                  <button onClick={() => setCartOpen(true)} data-testid="header-cart" title="Il tuo ordine"
                    className="relative w-9 h-9 rounded-full brand-bg grid place-items-center shadow-sm hover:opacity-90 transition-opacity">
                    <ShoppingBag size={16} className="text-white" />
                    {cart.count > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-white brand-text text-[10px] font-bold grid place-items-center shadow" data-testid="header-cart-count">{cart.count}</span>
                    )}
                  </button>
                  {isCustomer && (
                    <button onClick={toggleFav} data-testid="storefront-fav"
                      className={`w-9 h-9 rounded-full grid place-items-center border transition-colors ${fav ? "brand-soft brand-border" : "bg-card hover:bg-muted"}`}
                      title={fav ? "Rimuovi dai preferiti" : "Salva tra i preferiti"}>
                      <Heart size={16} className={fav ? "fill-current brand-text" : "text-muted-foreground"} />
                    </button>
                  )}
                  {installEvt && (
                    <Button size="sm" variant="outline" data-testid="install-pwa" className="rounded-full h-8"
                      onClick={async () => { installEvt.prompt(); await installEvt.userChoice; setInstallEvt(null); }}>
                      <Download size={14} className="mr-1" />App
                    </Button>
                  )}
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5"><Clock size={15} /> ~{store.prep_time} min prep.</span>
                {store.min_order > 0
                  ? <span className="flex items-center gap-1.5"><ShoppingBag size={15} /> min {fmt.eur(store.min_order)}</span>
                  : <span className="flex items-center gap-1.5 font-semibold" data-testid="sf-no-minimum"><ShoppingBag size={15} /> Senza minimo</span>}
                {(store.address || tenant.address) && <span className="flex items-center gap-1.5"><MapPin size={15} /> {store.address || tenant.address}</span>}
                {(store.phone || tenant.phone) && <span className="flex items-center gap-1.5"><Phone size={15} /> {store.phone || tenant.phone}</span>}
              </div>
              {store.paused && store.pause_message && <p className="mt-3 text-sm bg-[hsl(var(--warning))]/20 rounded-lg p-2">{store.pause_message}</p>}
              {config.promo && !promoClosed && (
                <div className="mt-4 flex items-center gap-2 rounded-xl brand-soft brand-text px-3 py-2.5 text-sm relative" data-testid="promo-banner">
                  <Tag size={16} className="shrink-0" />
                  <span className="pr-6">Usa il codice <b>{config.promo.code}</b> e ottieni {promoLabel(config.promo)}{config.promo.min_order ? ` su ordini da ${fmt.eur(config.promo.min_order)}` : ""}</span>
                  <button onClick={() => setPromoClosed(true)} className="absolute right-2 top-2.5 opacity-70 hover:opacity-100" data-testid="promo-close"><X size={16} /></button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* search + categories */}
        <div className="max-w-3xl mx-auto px-4 mt-5">
          <div className="relative">
            <Search className="absolute left-3 top-3 text-muted-foreground" size={18} />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nel menu" data-testid="menu-search" className="pl-10 h-11 rounded-full bg-card" />
          </div>
        </div>
        <div className="sticky top-0 z-30 backdrop-blur-xl bg-[hsl(var(--secondary))]/80 mt-3">
          <div className="max-w-3xl mx-auto px-4 py-2 flex gap-2 overflow-x-auto no-scrollbar">
            <button onClick={() => setActive(null)} className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap ${!active ? "brand-bg" : "bg-card border"}`}>Tutto</button>
            {cats.map((c) => (
              <button key={c.id} data-testid={`cat-${c.id}`} onClick={() => setActive(c.id)}
                      className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap ${active === c.id ? "brand-bg" : "bg-card border"}`}>{c.name}</button>
            ))}
          </div>
        </div>

        {/* products */}
        <div className="max-w-3xl mx-auto px-4 mt-4 grid sm:grid-cols-2 gap-3">
          {products.map((p, i) => {
            const soldOut = p.sold_out || !p.available;
            return (
              <motion.button key={p.id} data-testid={`product-${p.id}`} disabled={soldOut}
                onClick={() => !soldOut && setProduct(p)} whileTap={{ scale: soldOut ? 1 : 0.98 }}
                className={`text-left bg-card rounded-2xl border overflow-hidden flex ${soldOut ? "opacity-60" : "hover:-translate-y-1 transition-transform"}`}>
                <div className="flex-1 p-4">
                  <div className="flex gap-1.5 flex-wrap mb-1">
                    {p.labels?.map((l) => <Badge key={l} className="brand-soft brand-text text-[10px] font-semibold" variant="secondary">{l}</Badge>)}
                    {soldOut && <Badge variant="secondary" className="text-[10px]">Esaurito</Badge>}
                  </div>
                  <h3 className="font-head font-semibold">{p.name}</h3>
                  <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{p.description}</p>
                  <p className="font-bold mt-2 brand-text">{fmt.eur(p.price)}</p>
                </div>
                <img src={p.image_url || categoryImage(cats.find((c) => c.id === p.category_id)?.name, i)} alt={p.name} className="w-24 h-full object-cover" />
              </motion.button>
            );
          })}
        </div>

        {/* footer powered */}
        {config.powered_by && <p className="text-center text-xs text-muted-foreground mt-10">powered by <span className="font-semibold">Pickfood</span></p>}

        {/* sticky cart */}
        <AnimatePresence>
          {cart.count > 0 && (
            <motion.div initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }} className="fixed bottom-4 inset-x-0 px-4 z-40 no-print">
              <Button onClick={() => setCartOpen(true)} data-testid="open-cart"
                      className="max-w-3xl mx-auto w-full h-14 brand-bg hover:opacity-90 rounded-full font-semibold flex justify-between px-6 shadow-xl">
                <span className="flex items-center gap-2"><ShoppingBag size={20} /> {cart.count} articoli</span>
                <span>{fmt.eur(cart.total)}</span>
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        {product && <ProductDialog product={product} onClose={() => setProduct(null)} onAdd={cart.addItem} />}
        <CartSheet open={cartOpen} onClose={() => setCartOpen(false)} slug={slug} storeSlug={storeSlug} menu={menu} />
      </div>
    </ThemeApplier>
  );
}

export default function Storefront() {
  const { slug, storeSlug } = useParams();
  return <CartProvider slug={storeSlug ? `${slug}/${storeSlug}` : slug}><StorefrontInner slug={slug} storeSlug={storeSlug} /></CartProvider>;
}
